/** API job status key when the job is fully complete (display label: Complete). */
export const JOB_STATUS_COMPLETE_KEY = 'F0009541-D541-4BDA-8F12-AC3B168C060F';

export interface JobTimelineEntry {
  status: string;
  time: string | null;
  pendingText?: string | null;
  completedText?: string | null;
  isCompleted?: boolean;
  etaTimeUTC?: string | null;
  etaTimeLocalize?: string | null;
  remarks?: string | null;
}

export interface JobDetailsVendor {
  vendorKey: string;
  name: string;
  phone: string;
  email: string;
  address: string;
}

export interface JobDetailsJob {
  jobKey: string;
  jobName: string;
  description: string;
  entryDate: string;
  vendorStatus: string;
}

export interface JobDetailsAction {
  text: string;
  url: string;
  /** Set (by the API) when this check-in / check-out button must be done in the mobile app (RFI-363):
   * the portal shows the "download the app" prompt instead of opening `url`. */
  mobileAppStep?: 'check-in' | 'check-out' | null;
  /** Link that opens this job's check-in / check-out screen in the vendor app, e.g. `rfi-vendor://check-in/<jobKey>`. */
  mobileAppDeepLink?: string | null;
}

export interface JobDetailsEstimateLineItem {
  lineItemKey: string | null;
  isLabor: boolean;
  itemName: string | null;
  itemDescription: string | null;
  quantity: number | null;
  rate: number | null;
  total: number | null;
  changedInLatestRound: boolean;
  previousRate: number | null;
  previousQuantity: number | null;
  previousTotal: number | null;
  /** The note RFI typed against this line when it countered (shown beside the line); null/absent when there is none. */
  adminMessage?: string | null;
  /** True when this line changed in the latest round AND the change was admin-driven — drives the "Updated" badge. */
  isAdminUpdate: boolean;
  /** True when the vendor's rate is fixed to their profile rate — UI-only, the save endpoint re-enforces this. */
  isRateLocked: boolean;
  /** True only for trip-charge lines with a locked rate — quantity is fixed too. */
  isQuantityLocked: boolean;
  /** Labor lines: the real hours per technician and number of technicians (quantity is hours x technicians). */
  laborHours?: number | null;
  techs?: number | null;
  /** While RFI's counter is open: the most the vendor may send back (labor: total hours = hours x techs). */
  vendorMaxQty?: number | null;
  vendorMaxRate?: number | null;
}

export interface JobDetailsEstimateSummary {
  estimateKey: string | null;
  estimateNo: string | null;
  estimateStatus: string | null;
  estimateDate: string | null;
  estimateUpdatedDate: string | null;
  estimateTotal: number | null;
  /** UI-only signal — the backend edit endpoint re-checks this itself; never trust this alone. */
  canEdit: boolean;
  /** True only in Negotiate. UI-only signal — the approve endpoint re-checks this itself. */
  canApproveAsIs: boolean;
  isMultiOption: boolean;
  /** True when this is an on-site approval (tech is on-site awaiting cost approval) rather than a regular pre-work estimate. */
  isOnsiteApproval: boolean;
  /** The vendor's locked labor rate for this job, if configured — null when nothing is on file. */
  lockedLaborRate: number | null;
  /** The vendor's locked trip-charge rate for this job, if configured — null when nothing is on file. */
  lockedTripRate: number | null;
  /** The vendor's locked helper rate (standard or emergency, picked by the job type), if configured — null when nothing is on file. Absent on an older API build. */
  lockedHelperRate?: number | null;
  /** The standard / emergency labor and trip rates whatever the job's type, for the new-line "Labor / Emergency Labor" and "Trip / Emergency Trip"
   * pickers. null = none on file; absent (undefined) on an older API build. */
  lockedLaborRateStandard?: number | null;
  lockedLaborRateEmergency?: number | null;
  lockedTripRateStandard?: number | null;
  lockedTripRateEmergency?: number | null;
  /** Same for the new-line "Helper / Emergency Helper" picker (Emergency Helper = Helper Overtime/ Weekend Hourly Rate). */
  lockedHelperRateStandard?: number | null;
  lockedHelperRateEmergency?: number | null;
  /** The estimate option's title — multi-option estimates give each option its own. Absent on an older API build. */
  estimateTitle?: string | null;
  /** Key shared by all options of one multiple-option estimate — they are edited and resubmitted together. Absent on an older API build. */
  optionGroupKey?: string | null;
  /** The admin live-chat pill's label, word for word ("Negotiate", "Revision Requested", "Pending review", "Approved", ...). Changes with every admin action. */
  statusLabel?: string | null;
  /** pending_review | negotiate | revision_requested | agreed | approved | declined | customer_approval */
  stateCode?: string | null;
  /** The admin's note that came with the current state (counter / reject / decline). */
  adminNote?: string | null;
  /** Fingerprint of the current counter; sent back with "Accept and Agree". */
  counterVersion?: string | null;
  /** False while RFI's counter is open (only rejected-for-resubmission lets the vendor add lines again). */
  vendorCanAddLines?: boolean;
  lineItems: JobDetailsEstimateLineItem[];
}

export interface JobDetailsData {
  jobKey: string;
  po: string;
  vendorKey: string;
  /** When this matches the platform "Complete" status key, the job is fully closed out. */
  jobStatusKey?: string | null;
  currentStatus: string;
  currentStatusTime: string | null;
  timeline: JobTimelineEntry[];
  nextAction: string;
  vendor: JobDetailsVendor;
  job: JobDetailsJob;
  jobTypeName?: string;
  jobTypeKey?: string;
  actions?: JobDetailsAction[];
  estimates?: JobDetailsEstimateSummary[];
  /** Company-wide mobile-app check-in / check-out setting and store links (RFI-363). */
  mobileApp?: JobDetailsMobileApp;
}

export interface JobDetailsMobileApp {
  checkInRequiresApp: boolean;
  checkOutRequiresApp: boolean;
  /** Only the links that are configured; null when not set. */
  appStoreLink: string | null;
  playStoreLink: string | null;
  /** Android application id of the vendor app, used to build the `intent://` link. */
  androidPackage?: string;
}

export interface JobDetailsApiResponse {
  status: boolean;
  responseCode: number;
  message: string;
  data: JobDetailsData | null;
  details: unknown[];
  unixTime: number;
  traceId: string;
}

/** Result of parsing a job details API envelope (e.g. success vs. business-rule message). */
export type JobDetailsGetResult =
  | { outcome: 'success'; data: JobDetailsData }
  | { outcome: 'apiMessage'; message: string; responseCode: number }
  | { outcome: 'failure' };

/** Matches RFIJobOps CustomModel.VendorPortalLineChangeType — plain numeric enum, no string converter registered server-side. */
export const VENDOR_PORTAL_LINE_CHANGE = {
  existing: 2,
  new: 1,
  deleted: 3,
} as const;

export type VendorPortalLineChangeType =
  (typeof VENDOR_PORTAL_LINE_CHANGE)[keyof typeof VENDOR_PORTAL_LINE_CHANGE];

export interface VendorPortalEstimateLineEdit {
  changeType: VendorPortalLineChangeType;
  /** Required for existing/deleted lines; ignored by the backend for new lines. */
  lineItemKey: string;
  isLabor: boolean;
  itemName?: string | null;
  description?: string | null;
  rate: number;
  qty: number;
  techOnSite?: number | null;
  /** 1 = Proposed (negotiable), 0 = Incurred. Must be 1 for new lines added during negotiation. */
  costIncurred: number;
}

export interface VendorPortalEstimateEditRequest {
  estimateKey: string;
  vendorKey: string;
  lines: VendorPortalEstimateLineEdit[];
  vendorNotes?: string | null;
}

/** One option of a multiple-option estimate, with its own lines (new lines belong to this option only). */
export interface VendorPortalOptionEdit {
  estimateKey: string;
  lines: VendorPortalEstimateLineEdit[];
}

/** Whole multiple-option estimate saved as one batch (POST api/job/estimates/edit-batch). */
export interface VendorPortalMultiOptionEditRequest {
  vendorKey: string;
  options: VendorPortalOptionEdit[];
  vendorNotes?: string | null;
}

export interface VendorPortalEstimateEditApiResponse {
  status: boolean;
  responseCode: number;
  message: string;
  data: string | null;
  details: unknown[];
  unixTime: number;
  traceId: string;
}

export type VendorPortalEstimateEditResult =
  | { outcome: 'success'; message: string }
  | { outcome: 'apiMessage'; message: string; responseCode: number }
  | { outcome: 'failure' };

export interface VendorApproveEstimateRequest {
  vendorKey: string;
  /** The counter fingerprint the vendor was shown; accepting is refused if RFI changed the counter since. */
  counterVersion?: string | null;
}

export interface VendorApproveEstimateApiResponse {
  status: boolean;
  responseCode: number;
  message: string;
  data: unknown;
  details: unknown[];
  unixTime: number;
  traceId: string;
}

export type VendorApproveEstimateResult =
  | { outcome: 'success'; message: string }
  | { outcome: 'apiMessage'; message: string; responseCode: number }
  | { outcome: 'failure' };
